/**
 * Per-lift competition cards: grouping, frequency, includes, metrics, intensity zones.
 */

import { INTENSITY_ZONE_IDS } from "./insightsChartHelpers";
import { buildInsightsDisplayMetric } from "./insightsWeekOverview";
import {
  getExerciseMovementLabelForInsights,
  INSIGHTS_PER_LIFT_LENS_IDS,
  LENS_REQUIRED_LIFT_IDS,
  mapExerciseToLensLiftId,
} from "./insightsLensCompVolume";
import { liftBadgeMetaForKey } from "./insightsLiftBalance";

export { LIFT_CARD_BADGE_META } from "./insightsLiftBalance";
import {
  accumulateLiftWeekScalars,
  createEmptyLiftScalars,
} from "./insightsPerLiftScalars";
import { finalizeAvgPrescribedRpe } from "./insightsPrescribedRpe";
import {
  avgPrescribedEffortDisplayValue,
  formatAvgPrescribedEffortMetricDisplay,
  formatInsightsCountDisplay,
  formatInsightsLiftVolumeDisplay,
  formatInsightsPercentMetricDisplay,
  volumeDisplayValue,
} from "./insightsSetRowMath";

/** @typedef {import("./insightsLensCompVolume").InsightsPerLiftLens} InsightsPerLiftLens */
/** @typedef {ReturnType<typeof createEmptyLiftScalars>} LiftWeekScalars */

/** Function to get the display name for an exercise for insights.
 * @param {object} ex
 * @returns {string}
 */
function getExerciseDisplayNameForInsights(ex) {
  const movementLabel = getExerciseMovementLabelForInsights(ex);
  if (movementLabel) {
    return movementLabel;
  }
  const primary = ex.primary;
  if (typeof primary === "string" && primary.trim()) {
    if (primary === "overheadPress") {
      return "Overhead Press";
    }
    const p = primary.trim();
    return p.charAt(0).toUpperCase() + p.slice(1);
  }
  if (ex._id != null) {
    return String(ex._id);
  }
  return "Exercise";
}


/** Function to get unique include names for exercises.
 * @param {object[]} exercises
 * @returns {string[]}
 */
function uniqueIncludeNamesForExercises(exercises) {
  const seen = new Set();
  /** @type {string[]} */
  const names = [];
  for (const ex of exercises) {
    const name = getExerciseDisplayNameForInsights(ex);
    const key = name.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    names.push(name);
  }
  return names;
}


/** Function to group exercises by lens lift id.
 * @param {object[]} exercises
 * @param {InsightsPerLiftLens|string} lens
 * @returns {Map<string, object[]>}
 */
export function groupExercisesByLensLiftId(exercises, lens) {
  /** @type {Map<string, object[]>} */
  const groups = new Map();
  const required = LENS_REQUIRED_LIFT_IDS[lens] ?? [];
  for (const liftId of required) {
    groups.set(liftId, []);
  }
  for (const ex of exercises) {
    const liftId = mapExerciseToLensLiftId(ex, lens);
    if (!liftId || !groups.has(liftId)) {
      continue;
    }
    groups.get(liftId).push(ex);
  }
  return groups;
}

function buildIntensityZonesFromScalars(zones) {
  return INTENSITY_ZONE_IDS.map((zoneId) => ({
    zoneId,
    count: zones[zoneId] ?? 0,
  }));
}


/** Function to build lift metrics from scalars.
 * @param {LiftWeekScalars} cur
 * @param {LiftWeekScalars|null|undefined} prev
 * @param {{ fromMetric?: Function, unitsKey?: string, insightsVolumeSuffix?: string, intensityMeasure?: "rpe"|"rir" }} options
 * @returns {object[]}
 */
function buildLiftMetricsFromScalars(cur, prev, options) {
  const {
    fromMetric,
    unitsKey = "metric",
    insightsVolumeSuffix = " kg",
    intensityMeasure = "rpe",
  } = options;
  const avgCur = cur.pctCount > 0 ? cur.pctSum / cur.pctCount : 0;
  const avgPrev =
    prev && prev.pctCount > 0 ? prev.pctSum / prev.pctCount : null;
  const avgRpeCur = finalizeAvgPrescribedRpe(cur);
  const avgRpePrev = prev ? finalizeAvgPrescribedRpe(prev) : null;

  return [
    buildInsightsDisplayMetric({
      id: "sets",
      cur: cur.sets,
      prev: prev?.sets,
      formatDisplay: formatInsightsCountDisplay,
      toDeltaScalar: (v) => Math.round(v),
    }),
    buildInsightsDisplayMetric({
      id: "reps",
      cur: cur.reps,
      prev: prev?.reps,
      formatDisplay: formatInsightsCountDisplay,
      toDeltaScalar: (v) => Math.round(v),
    }),
    buildInsightsDisplayMetric({
      id: "totalWeight",
      cur: cur.volumeKg,
      prev: prev?.volumeKg,
      formatDisplay: (kg) =>
        formatInsightsLiftVolumeDisplay(
          kg,
          fromMetric,
          unitsKey,
          insightsVolumeSuffix
        ),
      toDeltaScalar: (kg) => volumeDisplayValue(kg, fromMetric, unitsKey),
    }),
    buildInsightsDisplayMetric({
      id: "percent1rm",
      cur: avgCur,
      prev: avgPrev,
      formatDisplay: formatInsightsPercentMetricDisplay,
      toDeltaScalar: (v) => Math.round(v),
    }),
    buildInsightsDisplayMetric({
      id: "avgPrescribedEffort",
      cur: avgRpeCur,
      prev: avgRpePrev,
      formatDisplay: (rpe) =>
        formatAvgPrescribedEffortMetricDisplay(rpe, intensityMeasure),
      toDeltaScalar: (rpe) =>
        avgPrescribedEffortDisplayValue(rpe, intensityMeasure),
    }),
    {
      id: "topIntensity",
      valueDisplay: formatInsightsPercentMetricDisplay(cur.peakPct),
    },
  ];
}


/** Function to build insights per lift cards for a lens.
 * @param {object[]} exercises
 * @param {InsightsPerLiftLens|string} lens
 * @param {Map<string, LiftWeekScalars>|null} prevScalarsByLiftId
 * @param {{ fromMetric?: Function, unitsKey?: string, insightsVolumeSuffix?: string, intensityMeasure?: "rpe"|"rir", dataSource?: string }} options
 * @param {Map<string, LiftWeekScalars>|null} precomputedScalarsByLiftId
 * @returns {object[]}
 */
export function buildInsightsPerLiftCardsForLens(
  exercises,
  lens,
  prevScalarsByLiftId = null,
  options = {},
  precomputedScalarsByLiftId = null
) {
  const { dataSource } = options;
  const groups = groupExercisesByLensLiftId(exercises, lens);
  const liftIds = LENS_REQUIRED_LIFT_IDS[lens] ?? [];
  /** @type {Map<string, LiftWeekScalars>} */
  const scalarsByLiftId = new Map();

  const cards = liftIds.map((liftId) => {
    const group = groups.get(liftId) ?? [];
    /** @type {Map<string, object>} */
    const uniqueById = new Map();
    for (const ex of group) {
      const key =
        ex._id == null
          ? getExerciseDisplayNameForInsights(ex)
          : String(ex._id);
      if (!uniqueById.has(key)) {
        uniqueById.set(key, ex);
      }
    }
    const uniqueExercises = [...uniqueById.values()];
    const exerciseCount = uniqueExercises.length;
    const notProgrammed = exerciseCount === 0;
    const meta = liftBadgeMetaForKey(liftId);

    let frequencyKey;
    if (exerciseCount === 0) {
      frequencyKey = "zeroWeek";
    } else if (exerciseCount === 1) {
      frequencyKey = "onceWeek";
    }

    const fromPrecomputed = precomputedScalarsByLiftId?.get(liftId);
    const scalars =
      fromPrecomputed ?? accumulateLiftWeekScalars(uniqueExercises, dataSource);
    scalarsByLiftId.set(liftId, scalars);

    const prevScalars = prevScalarsByLiftId?.get(liftId) ?? null;

    return {
      liftId,
      badgeLetter: meta.badgeLetter,
      badgeVariant: meta.badgeVariant,
      exerciseCount,
      frequencyKey,
      includesNames: notProgrammed
        ? []
        : uniqueIncludeNamesForExercises(uniqueExercises),
      includesNoteKey: false,
      notProgrammed,
      metrics: notProgrammed
        ? []
        : buildLiftMetricsFromScalars(scalars, prevScalars, options),
      intensityZones: notProgrammed
        ? []
        : buildIntensityZonesFromScalars(scalars.zones),
    };
  });

  return { cards, scalarsByLiftId };
}


/** Function to build all lens per lift cards for a week.
 * @param {object[]} exercises
 * @param {Map<string, LiftWeekScalars>|null} prevScalarsByLens
 * @param {{ fromMetric?: Function, unitsKey?: string, insightsVolumeSuffix?: string }} options
 * @param {Map<string, LiftWeekScalars>|null} precomputedScalarsByLens
 * @returns {object[]}
 */
export function buildAllLensPerLiftCardsForWeek(
  exercises,
  prevScalarsByLens = null,
  options = {},
  precomputedScalarsByLens = null
) {
  /** @type {Record<string, object[]>} */
  const perLiftCardsByLens = {};
  /** @type {Record<string, Map<string, LiftWeekScalars>>} */
  const scalarsByLens = {};

  for (const lensId of INSIGHTS_PER_LIFT_LENS_IDS) {
    const { cards, scalarsByLiftId } = buildInsightsPerLiftCardsForLens(
      exercises,
      lensId,
      prevScalarsByLens?.[lensId] ?? null,
      options,
      precomputedScalarsByLens?.[lensId] ?? null
    );
    perLiftCardsByLens[lensId] = cards;
    scalarsByLens[lensId] = scalarsByLiftId;
  }

  return { perLiftCardsByLens, scalarsByLens };
}
